// /opt/isp_erp/frontend/src/pages/warehouse/Inventory.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination
} from '@mui/material';
import { Search, Refresh, ArrowDownward, ArrowUpward } from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';

const Inventory = () => {
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('todos');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  useEffect(() => {
    cargarInventario();
  }, []);

  const cargarInventario = async () => {
    setLoading(true);
    try {
      const response = await warehouseAPI.listarEquipos();
      setEquipos(response.data || []);
    } catch (error) {
      console.error('Error cargando inventario:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colors = {
      BODEGA: 'success',
      EN_TRANSITO: 'warning',
      INSTALADO: 'info',
      RETIRO_PENDIENTE: 'error',
      RMA_BAJA: 'default'
    };
    return colors[estado] || 'default';
  };

  const getEstadoTexto = (estado) => {
    const textos = {
      BODEGA: 'En Bodega',
      EN_TRANSITO: 'En Tránsito',
      INSTALADO: 'Instalado',
      RETIRO_PENDIENTE: 'Retiro Pendiente',
      RMA_BAJA: 'RMA/Baja'
    };
    return textos[estado] || estado;
  };

  const filteredEquipos = equipos.filter(equipo => {
    const matchSearch = equipo.sn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       equipo.modelo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = estadoFilter === 'todos' || equipo.estado_actual === estadoFilter;
    return matchSearch && matchEstado;
  });

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(field);
  };

  const sortedEquipos = [...filteredEquipos].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (orderBy === 'created_at') {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    if (order === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const paginatedEquipos = sortedEquipos.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Inventario de Bodega
      </Typography>
      
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Buscar por SN o Modelo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estadoFilter}
                  onChange={(e) => setEstadoFilter(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="BODEGA">En Bodega</MenuItem>
                  <MenuItem value="EN_TRANSITO">En Tránsito</MenuItem>
                  <MenuItem value="INSTALADO">Instalado</MenuItem>
                  <MenuItem value="RETIRO_PENDIENTE">Retiro Pendiente</MenuItem>
                  <MenuItem value="RMA_BAJA">RMA/Baja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={cargarInventario}
                startIcon={<Refresh />}
              >
                Actualizar
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('sn')}
                  endIcon={orderBy === 'sn' && (order === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
                >
                  SN
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('modelo')}
                  endIcon={orderBy === 'modelo' && (order === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
                >
                  Modelo
                </Button>
              </TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Poseedor</TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSort('created_at')}
                  endIcon={orderBy === 'created_at' && (order === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
                >
                  Fecha Ingreso
                </Button>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : paginatedEquipos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No hay equipos registrados
                </TableCell>
              </TableRow>
            ) : (
              paginatedEquipos.map((equipo) => (
                <TableRow key={equipo.id} hover>
                  <TableCell>
                    <strong>{equipo.sn}</strong>
                  </TableCell>
                  <TableCell>{equipo.modelo}</TableCell>
                  <TableCell>
                    <Chip
                      label={getEstadoTexto(equipo.estado_actual)}
                      color={getEstadoColor(equipo.estado_actual)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{equipo.poseedor_nombre || 'Bodega'}</TableCell>
                  <TableCell>
                    {new Date(equipo.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredEquipos.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
};

export default Inventory;
